// client/src/components/pages/TelegramBI/ScheduleManager/ScheduleHistoryModal.tsx (converted from ScheduleHistoryModal.js, ST-00259)
//
// ST-00259: the load failure toast has a stable id ('telegram-schedule-history-load'). A
// filter change on a later page reloads twice (the filter, then the page reset), so a failing
// endpoint used to queue two identical toasts per click; now that the app renders toasts
// (AppToaster) it shows one. The unused default React import and icon were dropped.
import { useState, useEffect, useCallback } from 'react';
import {
  FaTimes,
  FaHistory,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaSpinner,
  FaRedo,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import type { AxiosResponse } from 'axios';
import api from '../../../../services/api';
import type {
  TelegramExecutionStatusFilter,
  TelegramExecutionTimeRange,
  TelegramScheduleExecution,
  TelegramScheduleExecutionBody,
  TelegramScheduleExecutionPage,
  TelegramScheduleExecutionParams,
  TelegramScheduleRow,
} from '../../../../types/clientPages/telegramSchedules';

export interface ScheduleHistoryModalProps {
  schedule: Pick<TelegramScheduleRow, 'schedule_id' | 'schedule_name'> | null | undefined;
  onClose: () => void;
}

const STATUS_FILTERS: TelegramExecutionStatusFilter[] = ['all', 'success', 'failed'];
const TIME_RANGES: { value: TelegramExecutionTimeRange; label: string }[] = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
];
const DAYS_BY_RANGE: Record<TelegramExecutionTimeRange, number> = { '24h': 1, '7d': 7, '30d': 30 };

/**
 * `data` of the history response: `{ success, data: page }` today; a bare page or a bare array
 * are still accepted, as the JavaScript did (`response.data?.data || response.data || {}`).
 */
const historyData = (
  body: TelegramScheduleExecutionBody | null | undefined
): TelegramScheduleExecutionPage | TelegramScheduleExecution[] => {
  if (!body) return {};
  if (!Array.isArray(body) && 'data' in body && body.data) return body.data;
  return body as TelegramScheduleExecutionPage | TelegramScheduleExecution[];
};

const ScheduleHistoryModal = ({ schedule, onClose }: ScheduleHistoryModalProps) => {
  const [executions, setExecutions] = useState<TelegramScheduleExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState<TelegramScheduleExecution | null>(
    null
  );

  // Filters
  const [statusFilter, setStatusFilter] = useState<TelegramExecutionStatusFilter>('all');
  const [timeRange, setTimeRange] = useState<TelegramExecutionTimeRange>('7d');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 20;

  const loadExecutions = useCallback(async () => {
    if (!schedule?.schedule_id) return;
    try {
      setLoading(true);

      const params: TelegramScheduleExecutionParams = {
        page: currentPage,
        limit: perPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        days: DAYS_BY_RANGE[timeRange] || undefined,
      };

      const response = (await api.telegramBI.schedules.getHistory(
        schedule.schedule_id,
        params
      )) as AxiosResponse<TelegramScheduleExecutionBody | null>;

      const data = historyData(response.data);
      const list = Array.isArray(data) ? data : data.executions || [];
      const total = (!Array.isArray(data) && data.totalCount) || list.length;
      const pages = (!Array.isArray(data) && data.totalPages) || Math.ceil(total / perPage) || 1;

      setExecutions(list);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (error) {
      console.error('Error loading execution history:', error);
      // A stable id: one toast however many reloads fail (ST-00259).
      toast.error('Failed to load execution history', {
        toastId: 'telegram-schedule-history-load',
      });
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  }, [schedule?.schedule_id, currentPage, statusFilter, timeRange]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, timeRange]);

  const handleRetry = async () => {
    try {
      // Retry is only offered on a listed execution, so a schedule is loaded.
      await api.telegramBI.schedules.execute(schedule!.schedule_id);
      toast.success('Schedule re-executed successfully');
      loadExecutions();
    } catch {
      toast.error('Failed to retry execution');
    }
  };

  const getStatusIcon = (success: boolean | null | undefined) =>
    success ? (
      <FaCheckCircle className="text-green-500" size={14} />
    ) : (
      <FaExclamationTriangle className="text-red-500" size={14} />
    );

  const getStatusBadgeClass = (success: boolean | null | undefined) =>
    success
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-red-50 text-red-700 border-red-200';

  const getStatusLabel = (success: boolean | null | undefined) => (success ? 'Success' : 'Failed');

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms && ms !== 0) return '--';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTime = (dateString: string | null | undefined) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <FaHistory className="text-primary-500" size={16} />
              <span>Execution History</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{schedule?.schedule_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden text-xs">
            {STATUS_FILTERS.map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-2.5 py-1.5 capitalize transition-colors ${
                  statusFilter === status
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface text-gray-600 hover:bg-gray-50'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Time Range */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden text-xs">
            {TIME_RANGES.map((range) => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={`px-2.5 py-1.5 transition-colors ${
                  timeRange === range.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          <div className="text-xs text-gray-500 ml-auto">
            {totalCount} execution{totalCount !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <FaSpinner className="animate-spin h-6 w-6 text-primary-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading history...</p>
              </div>
            </div>
          ) : executions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FaHistory className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No executions found</p>
              <p className="text-sm text-gray-400 mt-1">
                {statusFilter !== 'all' || timeRange !== '7d'
                  ? 'Try adjusting your filters'
                  : 'This schedule has not been executed yet'}
              </p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                  <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase">
                    Messages
                  </th>
                  <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase">
                    Duration
                  </th>
                  <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase">
                    Error
                  </th>
                  <th className="text-right pb-2 text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {executions.map((exec) => (
                  <tr
                    key={exec.schedule_execution_id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-800">
                        {formatTime(exec.execution_time)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex items-center space-x-1.5 px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadgeClass(exec.success)}`}
                      >
                        {getStatusIcon(exec.success)}
                        <span>{getStatusLabel(exec.success)}</span>
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-700">{exec.messages_sent ?? 0}</span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-gray-600 font-mono">
                        {formatDuration(exec.duration_ms)}
                      </span>
                    </td>
                    <td className="py-3 pr-4 max-w-[200px]">
                      {exec.error_message ? (
                        <span
                          className="text-xs text-red-600 truncate block"
                          title={exec.error_message}
                        >
                          {exec.error_message}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">--</span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => setSelectedExecution(exec)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                        title="View Details"
                      >
                        <FaEye size={12} />
                      </button>
                      {exec.success === false && (
                        <button
                          onClick={handleRetry}
                          className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors ml-1"
                          title="Retry"
                        >
                          <FaRedo size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-surface transition-colors"
              >
                <FaChevronLeft size={9} className="mr-0.5" /> Prev
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-surface transition-colors"
              >
                Next <FaChevronRight size={9} className="ml-0.5" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Execution Detail Overlay */}
      {selectedExecution && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4"
          onClick={() => setSelectedExecution(null)}
        >
          <div
            className="bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Execution Details</h3>
              <button
                onClick={() => setSelectedExecution(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500 text-xs">Execution ID</span>
                  <p className="font-mono text-gray-800 text-xs mt-0.5">
                    {selectedExecution.schedule_execution_id}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Status</span>
                  <p className="mt-0.5">
                    <span
                      className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadgeClass(selectedExecution.success)}`}
                    >
                      {getStatusIcon(selectedExecution.success)}
                      <span>{getStatusLabel(selectedExecution.success)}</span>
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Time</span>
                  <p className="text-gray-800 text-xs mt-0.5">
                    {formatTime(selectedExecution.execution_time)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Duration</span>
                  <p className="text-gray-800 text-xs font-mono mt-0.5">
                    {formatDuration(selectedExecution.duration_ms)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Messages Sent</span>
                  <p className="text-gray-800 text-xs mt-0.5">
                    {selectedExecution.messages_sent ?? 0}
                  </p>
                </div>
              </div>

              {selectedExecution.error_message && (
                <div>
                  <span className="text-xs font-medium text-gray-700">Error Message</span>
                  <div className="bg-red-50 border border-red-200 rounded p-3 mt-1">
                    <pre className="text-xs text-red-800 whitespace-pre-wrap">
                      {selectedExecution.error_message}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleHistoryModal;
